import { useState } from "react"

import { Alert, Collapse, Flex, Modal, Typography, Upload } from "antd"
import { useTranslation } from "react-i18next"

import Icon from "../../../../Icon"

import styles from "./IconPopover.module.less"

const { Text, Title } = Typography
const { Dragger } = Upload

/**
 * The two ways to set an icon: upload a file, or pick one from the library.
 *
 * Opened only from the Action menu — clicking the Icon cell itself does nothing. The upload
 * constraints are stated up front rather than after a rejection, and a rejected file reports the
 * rule it broke *inside* this dialog, which stays open with the file's context intact. Validation
 * failures deliberately never become toasts.
 */
const IconPopover = ({
  open,
  record,
  library,
  libraryFetching,
  uploadError,
  onUpload,
  onPick,
  onClose
}) => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState([])

  if (!record) return null

  const grid = (
    <div className={styles.grid}>
      {library.map((icon) => (
        <button
          key={icon.id}
          type="button"
          className={styles.gridItem}
          onClick={() => onPick(record, icon)}
          data-testid="payment-matching-library-icon"
        >
          <img src={icon.url} alt="" className={styles.gridImage} />
        </button>
      ))}
    </div>
  )

  return (
    <Modal
      open={open}
      onCancel={onClose}
      // Every modal gets a patterned background unless it opts out with clear-bg.
      classNames={{ content: "clear-bg" }}
      footer={null}
      width={520}
      title={
        <Title level={5} className={styles.title}>
          {record.icon_url ? t("Replace icon") : t("Add icon")}
          <Text type="secondary" className={styles.subtitle}>
            {record.current_name}
          </Text>
        </Title>
      }
    >
      <Dragger
        className={styles.dragger}
        multiple={false}
        showUploadList={false}
        accept=".svg,.png"
        beforeUpload={(file) => {
          onUpload(record, file)
          // Handled by the page's own request, so antd must not upload anything itself.
          return Upload.LIST_IGNORE
        }}
      >
        <Flex vertical align="center" gap={4}>
          <Icon type="arrow_inbox" className={styles.dropIcon} />
          <Text>{t("Drop an icon here or click to choose a file")}</Text>
          <Text type="secondary" className={styles.hint}>
            {t("SVG or PNG, square. PNG up to 32 KB and between 40x40 and 512x512 px.")}
          </Text>
        </Flex>
      </Dragger>

      {uploadError ? (
        <Alert type="error" showIcon className={styles.error} message={uploadError} />
      ) : null}

      <Collapse
        ghost
        activeKey={expanded}
        onChange={setExpanded}
        className={styles.library}
        items={[
          {
            key: "library",
            label: t("Choose from the library"),
            children: libraryFetching ? <Text type="secondary">{t("Loading")}...</Text> : grid
          }
        ]}
      />
    </Modal>
  )
}

export default IconPopover
